import logger from '../../app/logger';
import { useNavigateExtended } from '../../shared/hooks/navigate.hooks';
import { Page } from '../../shared/components/layout/Page';
import { SummaryPageContent, SummaryFormState } from '../summary/SummaryPageContent';

import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { recreateConfiguration } from '../widget/thunks/basic/recreateConfiguration';
import { App } from 'antd';
import { useLogikForm } from '../../shared/logik/hooks/form.hooks';
import { saveBuildingPackage } from './thunks/saveBuildingPackage';
import { publishEventAndRedirect } from '../../shared/utils/location.utils';
import {
    BuildingPackageDuplicateEvent,
    BuildingPackageDuplicateEventParams,
    BuildingPackageEditEvent,
    BuildingPackageEditEventParams,
    BuildingPackageNewEvent,
    BuildingPackageNewEventParams,
} from './building-packages.utils';
import { useCallback } from 'react';

export function BuildingPackageSummaryPage(): JSX.Element {
    const navigate = useNavigateExtended();
    const dispatch = useAppDispatch();
    const [form] = useLogikForm<SummaryFormState>();
    const { modal } = App.useApp();

    const launchObjectId = useAppSelector((state) => state.widget.init?.launchObjectId);
    const masterConfigurationId = useAppSelector((state) => state.widget.masterConfigurationId);
    const action = useAppSelector((state) => state.widget.init?.action);

    const onFinish = useCallback(
        async (values: unknown) => {
            try {
                const res = await dispatch(saveBuildingPackage()).unwrap();
                modal.confirm({
                    title: 'Building package has been successfully saved',
                    content: 'Now you can close the window or go back and make some changes',
                    icon: <></>,
                    okText: 'Save and continue',
                    cancelText: 'Save and close',
                    okButtonProps: { size: 'small' },
                    cancelButtonProps: { size: 'small' },
                    onOk: async () => await dispatch(recreateConfiguration(res.configuration.uuid)),
                    onCancel: async () => {
                        if (launchObjectId) {
                            if (!masterConfigurationId) {
                                await publishEventAndRedirect<BuildingPackageNewEventParams>(BuildingPackageNewEvent, {
                                    launchObjectId,
                                });
                            } else {
                                if (action === 'copy') {
                                    await publishEventAndRedirect<BuildingPackageDuplicateEventParams>(
                                        BuildingPackageDuplicateEvent,
                                        {
                                            launchObjectId,
                                        },
                                    );
                                } else {
                                    await publishEventAndRedirect<BuildingPackageEditEventParams>(
                                        BuildingPackageEditEvent,
                                        {
                                            launchObjectId,
                                        },
                                    );
                                }
                            }
                        }
                    },
                });
            } catch (err: unknown) {
                logger.error(err);
            }
        },
        [action, dispatch, launchObjectId, masterConfigurationId, modal],
    );

    return (
        <Page
            navProps={{
                onClickBack: () => navigate('..', { preserveSearch: true }),
                onClickNext: () => form.submit(),
                nextButtonTitle: 'Finish',
                hideSaveButton: true,
            }}>
            <SummaryPageContent onFinish={onFinish} form={form} />
        </Page>
    );
}
